import { Tab } from "@headlessui/react";
import { useMemo, useState } from "react";
import ExpectedSettlement from "./Expected";
import ReleasedSettlement from "./Released";
import SettledSettlement from "./Settled";
import SearchSettlement from "./Search";
import { useSelector } from "react-redux";
import { RootState } from "src/context/store";
import { List, FolderTree } from "lucide-react";
import { useSettlementFilters } from "./useSettlementFilters";
import usePermission from "src/hooks/usePermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";

const SettlementsHomePage = () => {
  const { filters } = useSelector((state: RootState) => state.settlement as any);
  const { EXPECTED: ExpectedFilters, RELEASED: ReleasedFilters, SETTLED: SettledFilters, SEARCH: SearchFilters } = filters;
  const [viewMode, setViewMode] = useState<'list' | 'tree'>('list');

  const handleViewChange = (selectedView: "list" | "tree") => {
    setViewMode(selectedView);
  }

  useSettlementFilters({
    EXPECTED: ExpectedFilters,
    RELEASED: ReleasedFilters,
    SETTLED: SettledFilters,
    SEARCH: SearchFilters
  });

  const { hasPermission: expectedSettlementPermission, loading: isExpectedPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.EXPECTED.module,
    action: fineGrainedPermissionsKeys.REPORTS.EXPECTED.action,
  });

  const { hasPermission: releasedSettlementPermission, loading: isReleasedPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.RELEASED.module,
    action: fineGrainedPermissionsKeys.REPORTS.RELEASED.action,
  });

  const { hasPermission: settledSettlementPermission, loading: isSettledPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.SETTLED.module,
    action: fineGrainedPermissionsKeys.REPORTS.SETTLED.action,
  });

  const { hasPermission: searchSettlementPermission, loading: isSearchSettlementPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.SEARCH.module,
    action: fineGrainedPermissionsKeys.REPORTS.SEARCH.action,
  });

  const isPermissionsLoading =
    isExpectedPermissionLoading ||
    isReleasedPermissionLoading ||
    isSettledPermissionLoading ||
    isSearchSettlementPermissionLoading;

  let tabs = [{
    title: "EXPECTED",
    component: <ExpectedSettlement viewMode={viewMode}
    />,
    condition: expectedSettlementPermission
  },
  {
    title: "RELEASED",
    component: <ReleasedSettlement viewMode={viewMode}
    />,
    condition: releasedSettlementPermission
  },
  {
    title: "SETTLED",
    component: <SettledSettlement viewMode={viewMode} />,
    condition: settledSettlementPermission
  },
  {
    title: "SEARCH",
    component: <SearchSettlement />,
    condition: searchSettlementPermission
  },
  ]

  const filteredTabs = useMemo(() => {
    return (tabs || []).filter((tab) => tab.condition !== false);
  }, [tabs, isPermissionsLoading]);

  return (
    <div>
      <section>
        <main>
          <div className="flex items-center justify-between px-[3.75rem] py-4 border-b border-b-[rgba(0, 0, 0, 0.10)]">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleViewChange('list')}
                title="List View"
                className={`p-2 rounded-md transition-colors ${viewMode === 'list'
                  ? 'bg-[#7F56D9] text-white'
                  : 'bg-transparent text-[#49484C] hover:bg-gray-100'
                  }`}
              >
                <List size={20} />
              </button>

              <button
                onClick={() => handleViewChange('tree')}
                title="Tree View"
                className={`p-2 rounded-md transition-colors ${viewMode === 'tree'
                  ? 'bg-[#7F56D9] text-white'
                  : 'bg-transparent text-[#49484C] hover:bg-gray-100'
                  }`}
              >
                <FolderTree size={20} />
              </button>
            </div>
          </div>

          {!isPermissionsLoading && (
            <Tab.Group>
              <div className="flex items-center justify-between px-[3.75rem] mt-6 border-b-[1px] border-b-[rgba(0, 0, 0, 0.10)] mb-[1.16rem]">
                <Tab.List>
                  {filteredTabs.map((item: any) =>
                    <Tab
                      className="disabled:cursor-not-allowed disabled:opacity-50"
                      key={item.title}
                    >
                      {({ selected }: { selected: boolean }) => (
                        <div
                          className={`relative inline-block  -bottom-[1px] px-4 py-3 font-semibold text-sm  border-b-2  not-italic  hover:border-b-[#586AF5] hover:text-[#586af5] focus:outline-none ${selected ? "text-[#586af5] border-b-[#586AF5]" : "text-[rgba(0,0,0,0.4)] border-b-transparent"
                            }`}
                        >
                          {item.title}
                        </div>
                      )}
                    </Tab>)}
                </Tab.List>
              </div>
              <Tab.Panels>
                {filteredTabs.map((item: any) =>
                  <Tab.Panel className="px-[3.75rem]" key={item.title}>
                    {item.component}
                  </Tab.Panel>
                )}
              </Tab.Panels>
            </Tab.Group>
          )}
        </main>
      </section>
    </div>
  );
};

export default SettlementsHomePage;
